import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, X } from 'lucide-react';
import { useInstallPWA } from './useInstallPWA';
import { InstallInstructionsModal } from './InstallInstructionsModal';

const DISMISS_KEY = 'manusplit_install_dismissed_at';
const DISMISS_DAYS = 30;
const INITIAL_DELAY_MS = 4000; // 4s tras carga para no molestar

/**
 * ¿Se dijo que no hace menos de treinta días?
 *
 * **La marca es una sola para el globo y para la fila**, y a propósito: decir
 * que no a instalar es una decisión sobre la aplicación, no sobre el sitio
 * donde te lo preguntaron. Con dos marcas, quien cerrara el globo en el móvil
 * se lo encontraría otra vez en el escritorio, que es la forma de que un aviso
 * deje de leerse.
 *
 * Nunca coinciden en pantalla —el globo es `lg:hidden` y la fila vive en la
 * lateral, que empieza en `lg`—, así que compartirla no tiene contrapartida.
 *
 * El `try` es por la navegación privada, donde `localStorage` lanza en vez de
 * devolver nada: sin almacenamiento se enseña, que es preferible a esconder
 * algo útil por no poder recordar una preferencia. Mismo criterio que
 * `AndroidAppLink`.
 */
function dismissedRecently(): boolean {
  try {
    const at = localStorage.getItem(DISMISS_KEY);
    if (!at) return false;
    return (Date.now() - Number(at)) / (1000 * 60 * 60 * 24) < DISMISS_DAYS;
  } catch {
    return false;
  }
}

function rememberDismissal(): void {
  try {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  } catch {
    /* ver arriba */
  }
}

/**
 * Invitar a instalar la app, en las dos formas que caben según dónde estemos.
 *
 * - `toast` (por omisión): el globo flotante de siempre, 4s tras cargar, si no
 *   está instalada, si el navegador da la talla y si no se descartó en los
 *   últimos 30 días. **Sólo por debajo de `lg`.**
 * - `row`: una fila más de la lateral, sin retardo pero **con aspa desde el
 *   2026-08-29**.
 *
 * ## Por qué el globo desaparece en escritorio
 *
 * Porque ahí hay lateral, y una cosa que flota por encima del contenido cuando
 * existe un sitio permanente donde ponerla es ruido. Es el mismo criterio que
 * ya seguía `NewExpenseFAB`, que es `lg:hidden` porque la lateral lleva su
 * botón. Además el globo se centraba respecto a la **ventana** y no respecto al
 * contenido, así que con los 240px de lateral salía descuadrado.
 *
 * ## Por qué una variante y no un componente aparte
 *
 * Lo que hay que saber para ofrecer la instalación —si ya está instalada, qué
 * plataforma es, qué instrucciones tocan— es lo mismo en los dos sitios. Con
 * dos componentes esa lógica se copiaba, y el día que cambie la heurística de
 * plataforma sólo se arreglaría uno.
 *
 * Lo que **no** comparten es el retardo: eso es del globo, que aparece solo e
 * interrumpe. Una fila de menú no interrumpe, así que sale en el acto.
 *
 * ## El descarte sí lo comparten, y antes no
 *
 * La fila nació sin aspa con el argumento de que no interrumpe. Falla en el
 * caso que no se puede detectar: **en iOS no hay forma de saber si la app ya
 * está instalada** desde el navegador —`navigator.standalone` sólo es cierto
 * *dentro* de la app instalada, y el evento `beforeinstallprompt` no existe
 * allí—, así que a quien la tenga en la pantalla de inicio y siga entrando por
 * Safari se le ofrecía instalarla para siempre, sin manera de callarlo.
 *
 * Con el aspa, ese caso lo cierra quien lo sufre. Y vuelve a los treinta días
 * por si cambió de móvil, que es el mismo trato que el globo — y no el «no
 * vuelve nunca» de `AndroidAppLink`, donde sí tiene sentido: allí lo que se
 * descarta es enterarse de que existe una app de Android, y eso no caduca.
 */
export function InstallPrompt({ variant = 'toast' }: { variant?: 'toast' | 'row' }) {
  const { t } = useTranslation();
  const { platform, installed, canPromptNative } = useInstallPWA();
  const [visible, setVisible] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [dismissed, setDismissed] = useState(() => dismissedRecently());

  useEffect(() => {
    if (variant !== 'toast') return;
    if (installed) return;
    if (platform === 'unknown') return;

    if (dismissedRecently()) return;

    const timeout = setTimeout(() => setVisible(true), INITIAL_DELAY_MS);
    return () => clearTimeout(timeout);
  }, [installed, platform, variant]);

  const handleDismiss = () => {
    rememberDismissal();
    setVisible(false);
  };

  const handleInstall = () => {
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
    // Si se abrió el modal, damos por consumido el prompt automático
    rememberDismissal();
    setVisible(false);
  };

  if (variant === 'row') {
    // **`platform === 'unknown'` no protege de nada aquí, y por eso no se usa.**
    // `detectPlatform()` cae en `'desktop'` por omisión: `'unknown'` sólo sale
    // sin `navigator`, o sea en pruebas y en render de servidor. En un
    // navegador real esa guarda no se dispara nunca.
    //
    // Lo que hay que preguntar es si **se puede instalar de verdad**:
    //
    //  - `canPromptNative` lo contesta en los navegadores basados en Chromium,
    //    que son los que disparan `beforeinstallprompt`.
    //  - iOS nunca dispara ese evento y aun así se instala, a mano, desde
    //    Compartir. Por eso va aparte.
    //  - En Firefox y en Safari de escritorio no se instala, así que aquí no
    //    hay nada que ofrecer. Con la guarda vieja la fila salía igual y
    //    explicaba un icono de la barra de direcciones que allí no existe.
    //
    // `dismissed` cierra el caso que ninguna de las tres detecta: iOS con la
    // app ya en la pantalla de inicio, donde el navegador no tiene forma de
    // saberlo. Ver §El descarte, arriba.
    if (installed || dismissed || !(canPromptNative || platform === 'ios')) return null;
    return (
      <>
        {/* Misma forma que `AndroidAppLink`, que es su vecina en el pie: el
            gesto principal ancho y el aspa pequeña al lado. Las medidas son las
            de las utilidades de la lateral —13px y no 14—, que es lo que son. */}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowModal(true)}
            className="flex min-w-0 flex-1 items-center gap-2 rounded-md px-3 py-2 text-[13px] text-muted transition-colors hover:bg-neutral-100 hover:text-brand"
          >
            <Download size={18} className="shrink-0" />
            {/* `prompt_cta` («Instalar») y no `title` («Instalar ManuSplit»): en
              una fila de 240px con icono, el nombre de la aplicación no cabe y
              además sobra —está escrito arriba, en la cabecera de la propia
              lateral—. Se reutiliza la cadena que ya existe para el botón del
              globo en vez de añadir una novena traducción de la misma palabra;
              el `title` largo se queda donde tiene sitio, que es el `h3` del
              modal. */}
            <span className="flex-1 truncate text-left">{t('install.prompt_cta')}</span>
          </button>
          <button
            type="button"
            onClick={() => {
              rememberDismissal();
              setDismissed(true);
            }}
            aria-label={t('common.close')}
            className="shrink-0 rounded p-1 text-muted transition-colors hover:text-ink"
          >
            <X size={14} />
          </button>
        </div>
        {showModal && <InstallInstructionsModal onClose={() => setShowModal(false)} />}
      </>
    );
  }

  return (
    <>
      {visible && (
        <div
          className="fixed inset-x-0 z-40 mx-auto flex max-w-md items-center gap-3 rounded-lg border border-brand-100 bg-white px-4 py-3 shadow-lg lg:hidden"
          style={{
            bottom: 'calc(env(safe-area-inset-bottom) + var(--nav-inferior) + 17px)',
            marginLeft: '1rem',
            marginRight: '1rem',
          }}
          role="status"
          aria-live="polite"
        >
          <Download size={20} className="shrink-0 text-brand" />
          <div className="flex-1 text-sm">
            <div className="font-medium">{t('install.prompt_title')}</div>
            <div className="text-xs text-muted">{t('install.prompt_hint')}</div>
          </div>
          <button
            type="button"
            onClick={handleInstall}
            className="btn-primary shrink-0 px-3 py-1.5 text-xs"
          >
            {t('install.prompt_cta')}
          </button>
          <button
            type="button"
            onClick={handleDismiss}
            aria-label={t('common.close')}
            className="shrink-0 text-muted hover:text-brand"
          >
            <X size={16} />
          </button>
        </div>
      )}
      {showModal && <InstallInstructionsModal onClose={handleModalClose} />}
    </>
  );
}
